import {useEventFormDefaultValues} from '@/features/agenda/hooks/useEventFormDefaultValues'
import {useGetEvent} from '@/features/agenda/queries/useGetEvent'
import {AgendaEventType} from '@/features/agenda/types'
import dayjs from 'dayjs'
import {useMemo} from 'react'

/**
 * Hook to get event into the AgendaEventType format
 * @param eventId - The id of the event to get
 * @returns The event and query states
 */
export const useAgendaEvent = ({eventId}: {eventId: string}) => {
    // Get event query
    const eventQuery = useGetEvent({
        eventId,
        responseType: 'extended'
    })

    // Get event data and default values
    const {data: eventData} = eventQuery
    const {defaultValues} = useEventFormDefaultValues({eventId})

    // Transform event to the AgendaEventType format
    const agendaEvent = useMemo((): AgendaEventType | null => {
        if (!eventData) return null

        let startDate = dayjs(eventData.date)
        startDate =
            eventData.time && eventData.time != '00:00:00'
                ? startDate
                      .set('hour', parseInt(eventData.time.split(':')[0]))
                      .set('minute', parseInt(eventData.time.split(':')[1]))
                : startDate.set('hour', 8).set('minute', 0)
        const endDate = startDate.add(eventData.duration ?? 0, 'minutes')

        if (eventData.type === 'event') {
            // Make sure status is not null for 'event' type
            const status = eventData.status || 'to_be_confirmed'
            return {
                ...eventData,
                status,
                start: startDate.toDate(),
                end: endDate.toDate(),
                isDraggable: status == 'tentative' ? false : true,
                resourceId: null
            } as AgendaEventType
        }

        return {
            ...eventData,
            start: startDate.toDate(),
            end: endDate.toDate(),
            isDraggable: false,
            resourceId: null
        } as AgendaEventType
    }, [eventData])

    return {
        ...eventQuery,
        agendaEvent,
        agendaEventDefaultValues: defaultValues
    }
}
